import { BoxProps } from "@mui/material";
import "azure-maps-control/dist/atlas.min.css";
import { useEffect, useState } from "react";
import { AzureMapsProvider } from "react-azure-maps";

import { GeoHelper } from "@/common/helpers/geo";
import { GeoMarker, GeoMarkerOptions } from "@/common/ts/geo";
import { GeoJsonPointInputDto } from "@/core/api/generated/v1";

import GeolocationMap from "./GeolocationMap";

interface Props {
  value: Nil<GeoJsonPointInputDto>;
  markerOptions?: Partial<GeoMarkerOptions>;
  sx?: BoxProps["sx"];
  onChange: (newValue: Nil<GeoJsonPointInputDto>) => void;
}

export default function GeolocationPointInput({ value, markerOptions, sx, onChange }: Props) {
  const [marker, setMarker] = useState<Nil<GeoMarker>>(
    GeoHelper.createMarkerFromGeoPoint({ point: value, markerOptions }),
  );

  useEffect(() => {
    const isCoordinatesChanged =
      value?.longitude !== marker?.coordinates?.longitude ||
      value?.latitude !== marker?.coordinates?.latitude;
    if (!isCoordinatesChanged) {
      return;
    }

    const sourceMarker =
      marker || GeoHelper.createMarkerFromGeoPoint({ point: value, markerOptions });
    const newMarker: Nil<GeoMarker> =
      value && sourceMarker && GeoHelper.isValidPointCoordinates(value)
        ? { ...sourceMarker, coordinates: value }
        : undefined;
    setMarker(newMarker);
  }, [value]);

  return (
    <AzureMapsProvider>
      <GeolocationMap
        sx={sx}
        markers={{
          markers: marker ? [marker] : [],
          max: 1,
          isZoomAtFirst: true,
          isCenterAtFirst: true,
          canAdd: true,
          canRemove: true,
          canDrag: true,
          onAdded: (newValue) => {
            const newMarker: GeoMarker = {
              ...newValue,
              options: {
                ...newValue.options,
                ...markerOptions,
              },
            };
            setMarker(newMarker);
            onChange(newMarker.coordinates);
          },
          onRemoved: () => {
            setMarker(undefined);
            onChange(undefined);
          },
          onChanged: (newValue) => {
            setMarker(newValue);
            onChange(newValue.coordinates);
          },
          onCoordinatesChanged: (newValue) => {
            setMarker(newValue);
            onChange(newValue.coordinates);
          },
        }}
        searchProps={{
          onCoordinatesFound: (newValue) => {
            if (marker) {
              const newMarker = {
                ...marker,
                coordinates: newValue,
              };
              setMarker(newMarker);
              onChange(newMarker.coordinates);
            } else {
              const newMarker = GeoHelper.createMarkerFromGeoPoint({
                point: newValue,
                markerOptions,
              });
              setMarker(newMarker);
              onChange(newMarker?.coordinates);
            }
          },
        }}
      />
    </AzureMapsProvider>
  );
}
